import IPPageNumber from "app/pages/InteractiveProposal/InteractiveProposalDownload/IPPageNuber";
import HexagonImg from "app/pages/InteractiveProposal/images/hexagonImg.png";
import { getSessionStore } from "app/utils/sessionStore";
import {
  createGradient,
  formatToCompactNumber,
  getThemeColors,
  getUSANumberFormat,
  projectionSDOptions,
} from "app/utils/utils";
import { getColorVariations } from "app/utils/colorUtils";
import { Chart } from "primereact/chart";
import { lineChartOptions } from "app/uicomponents/macrocomponents/PlansSummary/config";

interface PerformanceMetrics {
  actualAccountValue: number;
  actualSurrenderValue: number;
  actualDeathBenefit: number;
  actualFinancingBalance: number;
  totalOutsideAssets: number;
  interestCredits: number;
}

interface PerformanceRow {
  planYear: number;
  preCredit: PerformanceMetrics;
  postCredit: PerformanceMetrics;
}

interface IRCPerformancePageProps {
  performance: PerformanceRow[];
  creditType?: string;
  pageNumber: string | number;
}

const tableHeaders = [
  { label: "Plan Year" },
  { label: "Account Value" },
  { label: "Surrender Value" },
  { label: "Death Benefit" },
  { label: "Financing Balance" },
  { label: "Outside Assets" },
  { label: "Interest Credits" },
];

const IRCPerformancePage = ({
  performance,
  creditType = "postCredit",
  pageNumber,
}: IRCPerformancePageProps) => {
  const { branding } = getSessionStore("lookUpResponse");
  const { primaryColor, ipTooltipColor, secondaryColor } = getThemeColors(branding);

  const planYears = performance.map((r) => r.planYear);
  const getMetrics = (r: PerformanceRow): PerformanceMetrics =>
    creditType === "postCredit" ? r.postCredit : r.preCredit;

  const accountValues = performance.map((r) => getMetrics(r).actualAccountValue);
  const surrenderValues = performance.map((r) => getMetrics(r).actualSurrenderValue);
  const deathBenefits = performance.map((r) => getMetrics(r).actualDeathBenefit);
  const financingBalances = performance.map((r) => getMetrics(r).actualFinancingBalance);

  const latestIdx = performance.length - 1;
  const latestMetrics = latestIdx >= 0 ? getMetrics(performance[latestIdx]) : null;

  const makeChart = (label: string, data: number[], colorIdx: number) => ({
    labels: planYears,
    datasets: [
      {
        label,
        fill: true,
        borderColor: projectionSDOptions(primaryColor)[colorIdx].indicatorColor,
        backgroundColor: (context: any) =>
          createGradient(context, projectionSDOptions(primaryColor)[colorIdx].indicatorColor),
        yAxisID: "y",
        tension: 0.4,
        data,
      },
    ],
  });

  const accountChart = makeChart("Account Value", accountValues, 2);
  const surrenderChart = makeChart("Surrender Value", surrenderValues, 0);
  const deathBenefitChart = makeChart("Death Benefit", deathBenefits, 3);
  const financingChart = {
    labels: planYears,
    datasets: [
      {
        label: "Financing Balance",
        fill: true,
        borderColor: projectionSDOptions(primaryColor)[4].indicatorColor,
        backgroundColor: (context: any) =>
          createGradient(context, projectionSDOptions(primaryColor)[4].indicatorColor),
        yAxisID: "y",
        tension: 0.4,
        data: financingBalances,
      },
    ],
  };

  const colorVariations = getColorVariations(secondaryColor, tableHeaders.length).reverse();
  const creditLabel = creditType === "postCredit" ? "Post-Credit" : "Pre-Credit";

  const ChartCard = ({
    title,
    value,
    chartData,
    tooltipColor = ipTooltipColor,
  }: {
    title: string;
    value: string;
    chartData: any;
    tooltipColor?: string;
  }) => (
    <div className="flex-1">
      <div className="flex justify-between conthrax-font">
        <p className="font-bold text-gray-800 text-xs">{title}</p>
        <span className="text-xs font-bold text-gray-500">{value}</span>
      </div>
      <Chart
        type="line"
        height="140px"
        width="240px"
        data={chartData}
        options={lineChartOptions(false, tooltipColor, "Plan Year", "Plan Year")}
      />
    </div>
  );

  return (
    <div className="flex flex-col h-full relative min-h-screen">
      <div className="relative z-20 px-[2em] w-full max-w-[56em] mx-auto p-[1.75em]">
        <h1 className="text-center text-2xl font-bold mb-3 conthrax-font interactive-proposal-text">
          Performance ({creditLabel})
        </h1>

        {/* Row 1: Account Value + Surrender Value */}
        <div className="flex gap-4 mb-2">
          <ChartCard
            title="Account Value"
            value={
              latestMetrics ? `$${formatToCompactNumber(latestMetrics.actualAccountValue)}` : ""
            }
            chartData={accountChart}
          />
          <ChartCard
            title="Surrender Value"
            value={
              latestMetrics ? `$${formatToCompactNumber(latestMetrics.actualSurrenderValue)}` : ""
            }
            chartData={surrenderChart}
          />
        </div>

        {/* Row 2: Death Benefit + Financing Balance */}
        <div className="flex gap-4 mb-3">
          <ChartCard
            title="Death Benefit"
            value={
              latestMetrics ? `$${formatToCompactNumber(latestMetrics.actualDeathBenefit)}` : ""
            }
            chartData={deathBenefitChart}
          />
          <ChartCard
            title="Financing Balance"
            value={
              latestMetrics ? `$${formatToCompactNumber(latestMetrics.actualFinancingBalance)}` : ""
            }
            chartData={financingChart}
            tooltipColor="#808080"
          />
        </div>

        {/* Performance table */}
        <table className="w-full border-collapse">
          <thead className="text-white">
            <tr>
              {tableHeaders.map((h, i) => (
                <th
                  key={i}
                  className="p-1 text-center pdf_table_border border-white font-semibold"
                  style={{ backgroundColor: colorVariations[i], fontSize: "9px" }}
                >
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {performance.map((row, i) => {
              const m = getMetrics(row);
              return (
                <tr
                  key={i}
                  style={{ backgroundColor: "#EEEEEE", color: "#000000", fontSize: "9px" }}
                >
                  <td className="p-1 text-center pdf_table_border border-white">{row.planYear}</td>
                  <td className="p-1 text-center pdf_table_border border-white">
                    ${getUSANumberFormat(m.actualAccountValue, false)}
                  </td>
                  <td className="p-1 text-center pdf_table_border border-white">
                    ${getUSANumberFormat(m.actualSurrenderValue, false)}
                  </td>
                  <td className="p-1 text-center pdf_table_border border-white">
                    ${getUSANumberFormat(m.actualDeathBenefit, false)}
                  </td>
                  <td className="p-1 text-center pdf_table_border border-white">
                    ${getUSANumberFormat(m.actualFinancingBalance, false)}
                  </td>
                  <td className="p-1 text-center pdf_table_border border-white">
                    ${getUSANumberFormat(m.totalOutsideAssets, false)}
                  </td>
                  <td className="p-1 text-center pdf_table_border border-white">
                    ${getUSANumberFormat(m.interestCredits, false)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="absolute bottom-0 left-0 w-full">
        <div
          className="h-[350px] bg-cover bg-center"
          style={{ backgroundImage: `url(${HexagonImg})` }}
        />
        <div className="px-[4em] w-full max-w-[56em] mx-auto p-[1.75em] pt-1">
          <IPPageNumber number={pageNumber} />
        </div>
      </div>
    </div>
  );
};

export default IRCPerformancePage;
